import moment from 'moment';
import { Link } from 'react-router-dom';

import { AuditTrail } from '../../../../../../../backend/src/auditTrail/auditTrail.entity';
import { AuditEventType } from '../../../../../../../backend/src/auditTrail/auditTrail.types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { formatAuditEventType } from '@/new/pages/components/audit-trail-filter-popover';
import { getAuditEntityUrl } from '@/new/pages/components/audit-trail-table';

// Lucide icon per event type shown in the recent-activity list. Falls back to a neutral icon for
// any event type not mapped here.
const ACTIVITY_ICONS: Partial<Record<AuditEventType, string>> = {
  [AuditEventType.SubsetCreate]: 'lucide--group',
  [AuditEventType.ChronologyCreate]: 'lucide--git-fork',
  [AuditEventType.DocChronologyCreate]: 'lucide--folder-git-2',
};

function getActivityIcon(audit: AuditTrail): string {
  return ACTIVITY_ICONS[audit.auditEventType as AuditEventType] ?? 'lucide--circle-plus';
}

// Every event in RECENT_ACTIVITY_EVENT_TYPES carries a `name` in its detail, but the AuditDetail
// union is broader than that, so narrow defensively.
function getActivityName(audit: AuditTrail): string | null {
  const detail = audit.auditDetail;
  return 'name' in detail ? detail.name : null;
}

// Chronology/doc-chronology events carry the subset they belong to in their detail.
function getActivitySubsetId(audit: AuditTrail): string | null {
  const detail = audit.auditDetail;
  return 'subsetId' in detail ? detail.subsetId : null;
}

export function RecentActivity({
  matterId,
  subsetId,
  activity,
}: {
  matterId: string;
  subsetId?: string;
  activity: AuditTrail[];
}) {
  const recentActivity = activity;

  const { data: subsets } = useGetSubsets(matterId);
  // Resolve a non-all-documents subset by id, so we can show its name as a chip. The all-documents
  // subset is conceptually the matter itself, so we skip the chip for it.
  const getSubsetName = (subsetId: string | null): string | null => {
    if (!subsetId) return null;
    const subset = subsets?.find((s) => s.id === subsetId);
    return subset && !subset.isAllDocuments ? subset.name : null;
  };

  return (
    <div className="my-5 mt-5.5">
      <h2 className="mb-2 font-medium">
        {subsetId !== undefined ? 'Your recent Subset activity' : 'Your recent activity'}
      </h2>

      <div className="-mx-1.75 flex flex-col gap-px rounded-lg text-[13px]">
        {recentActivity.length > 0 &&
          recentActivity.map((audit) => {
            const url = getAuditEntityUrl(audit);
            const name = getActivityName(audit);
            const label = formatAuditEventType(audit.auditEventType);
            const subsetName = getSubsetName(getActivitySubsetId(audit));

            const content = (
              <div className="flex items-center justify-between gap-2 rounded-md px-2 py-0.75 transition-colors hover:bg-gray-50">
                <div className="flex min-w-0 items-center gap-1.75">
                  <Icon className={`${getActivityIcon(audit)} shrink-0 text-gray-500/80`} />
                  {subsetName && (
                    <Tooltip>
                      <TooltipTrigger className="flex max-w-20 shrink-0 items-center gap-1 rounded-md bg-sky-50 px-1 py-0.5">
                        <Icon className="lucide--group text-sky-500" />
                        <span className="truncate text-[11px] text-sky-500">{subsetName}</span>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={6}>{subsetName}</TooltipContent>
                    </Tooltip>
                  )}
                  <p className="line-clamp-1 text-gray-700">
                    {label}
                    {name && <span className="font-medium text-gray-900">: {name}</span>}
                  </p>
                </div>
                <p className="shrink-0 text-xs text-gray-500">{moment(audit.eventDate).fromNow()}</p>
              </div>
            );

            return url ? (
              <Link
                key={audit.id}
                to={url}
                onClick={() =>
                  trackCustomEvent(TrackingEventsV2.MatterClickRecentActivity, {
                    matterId,
                    subsetId,
                    entityType: audit.auditEventType,
                  })
                }
              >
                {content}
              </Link>
            ) : (
              <div key={audit.id}>{content}</div>
            );
          })}
      </div>
    </div>
  );
}
